import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';

import {
  arShapesQueryOptions,
  blastByNameQueryOptions,
  blocksQueryOptions,
  drillDomainsQueryOptions,
  drilledDataQueryOptions,
  drillHolesQueryOptions,
} from '~api/queries';
import type {
  ArShapesResponse,
  Blast,
  BlocksResponse,
  DrillDomainsResponse,
  DrilledDataResponse,
  DrillHolesResponse,
} from '~api/services';
import { type LayerVisibility, useLayerVisibility } from '~libs';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type LayerVisibilities = {
  blast: LayerVisibility;
  blocks: LayerVisibility;
  drillDomains: LayerVisibility;
  holes: LayerVisibility;
  neighbouringDrillHoles: LayerVisibility;
  arShapes: LayerVisibility;
};

type QueryState<TData> = {
  data: TData | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
};

type UseBlastDesignContextLayersOptions = {
  /** Controls the blocks query — driven by user selection in the sidebar */
  selectedModelName: string | undefined;
};

type UseBlastDesignContextLayersResult = {
  blast: Blast;
  blocksQuery: QueryState<BlocksResponse>;
  drilledDataQuery: QueryState<DrilledDataResponse>;
  drillDomainsQuery: QueryState<DrillDomainsResponse>;
  neighbouringDrillHolesQuery: QueryState<DrillHolesResponse>;
  arShapesQuery: QueryState<ArShapesResponse>;
  layers: LayerVisibilities;
  haveBlocks: boolean;
  haveDrillDomains: boolean;
  haveDrilledHoles: boolean;
  haveNeighbouringDrillHoles: boolean;
  haveArShapes: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                  Main Hook                                 */
/* -------------------------------------------------------------------------- */

/**
 * Aggregates all canvas layer data fetching, visibility state, presence flags, and
 * auto-hide effects for the Blast Design Context page.
 *
 * **Why a single hook?** The layers share a cross-cutting concern: each must
 * auto-hide when its query returns an empty data set. Centralising that in one
 * place keeps the relationship explicit and the provider focused on UI state
 * orchestration and derived data only.
 *
 * **Blast data** is read from cache via `useSuspenseQuery` — the route loader
 * guarantees it is present, so there is no network call.
 *
 * **Layer auto-hide strategy:**
 * - Initially-visible layers (`blast`, `blocks`, `drilledHoles`, `arShapes`) sync bidirectionally so they
 *   reappear when data loads in.
 * - Initially-hidden layers (`drillDomains`, `neighbouringDrillHoles`) sync unidirectionally so they remain hidden until the user explicitly toggles them on, even if data is present.
 */
const useBlastDesignContextLayers = ({
  selectedModelName,
}: UseBlastDesignContextLayersOptions): UseBlastDesignContextLayersResult => {
  const { blastName } = useParams({ strict: false });
  if (!blastName) throw new Error('blastName is required');

  // Layer visibility state
  const blastLayerVisibility = useLayerVisibility();
  const blocksModelVisibility = useLayerVisibility();
  const drillDomainsVisibility = useLayerVisibility({ visible: false });
  const drilledHolesVisibility = useLayerVisibility();
  const neighbouringDrillHolesVisibility = useLayerVisibility({ visible: false });
  const arShapesVisibility = useLayerVisibility();

  // Cache hit — guaranteed by the route loader's ensureQueryData
  const { data: blast } = useSuspenseQuery(blastByNameQueryOptions(blastName));

  const blocksQuery = useQuery(blocksQueryOptions({ blast_name: blastName, model_name: selectedModelName }));

  const drilledDataQuery = useQuery(
    drilledDataQueryOptions({
      pattern: blast.name,
      mine_code: blast.mine_mas_code,
      crs: 'local',
    }),
  );

  const drillDomainsQuery = useQuery(drillDomainsQueryOptions({ pattern: blast.name, crs: 'local' }));

  const neighbouringDrillHolesQuery = useQuery(drillHolesQueryOptions({ blast_name: blast.name }));

  const arShapesQuery = useQuery(
    arShapesQueryOptions({
      blast_name: blast.name,
      mine_site: blast.mine_mas_code,
      crs: 'local',
    }),
  );

  // Derived presence flags
  const haveBlocks = !!blocksQuery.data?.blocks?.length;
  const haveDrillDomains = !!drillDomainsQuery.data?.features?.length;
  const haveDrilledHoles = !!drilledDataQuery.data?.drilled_data?.length;
  const haveArShapes = !!arShapesQuery.data?.features?.length;
  // Count only valid holes to match what NeighbouringDrillHolesLayer actually renders.
  // Using raw length would keep the layer visible even when all holes are invalid and nothing renders.
  const haveNeighbouringDrillHoles = !!neighbouringDrillHolesQuery.data?.drill_holes?.some((h) => h.valid === 'valid');

  const { setVisible: setBlocksModelVisible } = blocksModelVisibility;
  const { setVisible: setDrillDomainsVisible } = drillDomainsVisibility;
  const { setVisible: setDrilledHolesVisible } = drilledHolesVisibility;
  const { setVisible: setArShapesVisible } = arShapesVisibility;
  const { setVisible: setNeighbouringDrillHolesVisible } = neighbouringDrillHolesVisibility;

  // Bidirectional sync: each layer tracks its own data-presence flag.
  // When data arrives the layer becomes visible; when data is absent it hides.
  // User toggles are preserved between data changes because each effect only
  // re-runs when *its own* presence flag changes — an unrelated layer's data
  // update cannot override another layer's toggle state.
  //
  // NOTE: For unidirectional sync (auto-hide only — the layer does NOT auto-show
  // when data returns, so the user's "visible" toggle is always preserved), use a
  // dedicated effect:
  //
  //   useEffect(() => {
  //     if (!condition) setLayerVisible(false);
  //   }, [condition, setLayerVisible]);

  useEffect(() => {
    setBlocksModelVisible(haveBlocks);
  }, [haveBlocks, setBlocksModelVisible]);

  useEffect(() => {
    setDrilledHolesVisible(haveDrilledHoles);
  }, [haveDrilledHoles, setDrilledHolesVisible]);

  useEffect(() => {
    if (!haveDrillDomains) setDrillDomainsVisible(false);
  }, [haveDrillDomains, setDrillDomainsVisible]);

  useEffect(() => {
    if (!haveNeighbouringDrillHoles) setNeighbouringDrillHolesVisible(false);
  }, [haveNeighbouringDrillHoles, setNeighbouringDrillHolesVisible]);

  useEffect(() => {
    setArShapesVisible(haveArShapes);
  }, [haveArShapes, setArShapesVisible]);

  const layers = useMemo(
    () => ({
      blast: blastLayerVisibility,
      blocks: blocksModelVisibility,
      drillDomains: drillDomainsVisibility,
      holes: drilledHolesVisibility,
      neighbouringDrillHoles: neighbouringDrillHolesVisibility,
      arShapes: arShapesVisibility,
    }),
    [
      blastLayerVisibility,
      blocksModelVisibility,
      drillDomainsVisibility,
      drilledHolesVisibility,
      neighbouringDrillHolesVisibility,
      arShapesVisibility,
    ],
  );

  return {
    blast,
    blocksQuery,
    drilledDataQuery,
    drillDomainsQuery,
    neighbouringDrillHolesQuery,
    arShapesQuery,
    layers,
    haveBlocks,
    haveDrillDomains,
    haveDrilledHoles,
    haveArShapes,
    haveNeighbouringDrillHoles,
  };
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { LayerVisibilities, QueryState };
export { useBlastDesignContextLayers };
